import { useMemo } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Tabs from "@/v2-backoffice-client/components/tabs"
import type {
  ChurnRiskLevel,
  UsageScopeSummary,
} from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import {
  usePortfolioTimeframe,
  type PortfolioFilters,
} from "@/v2-backoffice-client/portfolio/portfolio.filters"
import type { PortfolioCustomerSummary } from "@/v2-backoffice-client/portfolio/portfolio.utils"
import type { PortfolioRiskCounts } from "@/v2-backoffice-client/portfolio/portfolio.health.utils"
import { derivePortfolioPlays } from "@/v2-backoffice-client/portfolio/portfolio.plays.utils"
import { usePortfolioDailyData } from "@/v2-backoffice-client/portfolio/portfolio.daily.data"
import PortfolioOverviewSection from "@/v2-backoffice-client/portfolio/PortfolioOverviewSection"
import PortfolioHealthSection from "@/v2-backoffice-client/portfolio/PortfolioHealthSection"
import PortfolioPlaybook from "@/v2-backoffice-client/portfolio/PortfolioPlaybook"
import PortfolioGeoSection from "@/v2-backoffice-client/portfolio/PortfolioGeoSection"
import PortfolioMcpSection from "@/v2-backoffice-client/portfolio/PortfolioMcpSection"

export type PortfolioSubTabsProps = {
  /** The globally filtered book — every sub-tab reads the same set. */
  customers: PortfolioCustomerSummary[]
  /** The book rolled up as one scope. */
  fleet: UsageScopeSummary
  /** Churn donut counts — facet-narrowed except for the churn facet itself. */
  riskCounts: PortfolioRiskCounts
  filters: PortfolioFilters
  onChurnLevelsChange(levels: ChurnRiskLevel[]): void
}

const PORTFOLIO_TABS_ID = "portfolio"
const DEFAULT_SUB_TAB = "overview"

/**
 * The sign-in/daily queries filter by customer id server-side but can't
 * evaluate the derived facets (churn, score). When facets are active the
 * client resolves them to the matching customer ids; an empty result must
 * still filter (not fall back to "everyone"), so it sends one impossible id.
 */
const NO_CUSTOMER_SENTINEL = "-"

// The Portfolio's sub-tab views over the already-filtered book, split so
// each view gets a full screen instead of one long scroll: Overview (pulse),
// Health (per-account diagnostics), Playbook (the weekly plan), Geography
// (the two maps), MCP (the AI-client product: who uses it, who is refused).
// Owns the ONE daily-rows query the time-series surfaces share; the MCP tab
// owns its own aggregated read (it mounts only when shown); everything else
// arrives derived from the portfolio query upstream.
function PortfolioSubTabs(props: PortfolioSubTabsProps) {
  const { customers, fleet, riskCounts, filters, onChurnLevelsChange } = props

  // Per-section timeframes — only the two time-series surfaces re-window;
  // everything profile-derived is computed upfront in Databricks on a fixed
  // 28-day window and deliberately carries no time selector.
  const activityTimeframe = usePortfolioTimeframe("pf-window")

  const hasFacetFilters =
    filters.owners.length > 0 ||
    filters.churnLevels.length > 0 ||
    filters.scoreLevels.length > 0 ||
    filters.licenseStatusCodes.length > 0
  // Server-side chart/geo queries follow the facets via the resolved ids.
  const effectiveCustomerIds = useMemo(() => {
    if (!hasFacetFilters) {
      return filters.customerIds.length > 0 ? filters.customerIds : null
    }
    if (customers.length === 0) return [NO_CUSTOMER_SENTINEL]
    return customers.map((customer) => customer.externalCustomerId)
  }, [hasFacetFilters, filters.customerIds, customers])

  const daily = usePortfolioDailyData({
    timeWindow: activityTimeframe.window,
    customerIds: effectiveCustomerIds,
    excludeCustomerIds: filters.excludeCustomerIds,
    excludeUserIds: filters.excludeUserIds,
  })

  const plays = useMemo(() => derivePortfolioPlays(customers), [customers])

  function toggleChurnLevel(level: ChurnRiskLevel) {
    onChurnLevelsChange(
      filters.churnLevels.includes(level)
        ? filters.churnLevels.filter((current) => current !== level)
        : [...filters.churnLevels, level],
    )
  }

  return (
    <Tabs
      id={PORTFOLIO_TABS_ID}
      defaultValue={DEFAULT_SUB_TAB}
      // gap, not a margin on each content: TabsContent sets its own margin
      // with an attribute variant, which outranks a plain `mt-*` from here.
      className="flex min-h-0 flex-1 flex-col gap-4"
    >
      <ErrorMessage error={daily.error} />

      <Tabs.List className="self-start">
        <Tabs.Trigger value="overview">Overview</Tabs.Trigger>
        <Tabs.Trigger value="health">Health</Tabs.Trigger>
        <Tabs.Trigger value="playbook">Playbook</Tabs.Trigger>
        <Tabs.Trigger value="geography">Geography</Tabs.Trigger>
        <Tabs.Trigger value="mcp">MCP</Tabs.Trigger>
      </Tabs.List>

      <Tabs.Content value="overview" className="min-h-0 flex-1 overflow-y-auto">
        <PortfolioOverviewSection
          customers={customers}
          fleet={fleet}
          areaDays={daily.areaDays}
          timeWindow={activityTimeframe.window}
          timeframeKey={activityTimeframe.key}
          onTimeframeChange={activityTimeframe.setKey}
          riskCounts={riskCounts}
          activeChurnLevels={filters.churnLevels}
          onChurnLevelToggle={toggleChurnLevel}
        />
      </Tabs.Content>

      <Tabs.Content value="health" className="min-h-0 flex-1 overflow-y-auto">
        <PortfolioHealthSection
          customers={customers}
          fleet={fleet}
          areaDays={daily.areaDays}
          customerIds={effectiveCustomerIds}
          excludeCustomerIds={filters.excludeCustomerIds}
          excludeUserIds={filters.excludeUserIds}
        />
      </Tabs.Content>

      <Tabs.Content value="playbook" className="min-h-0 flex-1 overflow-y-auto">
        <PortfolioPlaybook plays={plays} />
      </Tabs.Content>

      <Tabs.Content value="geography" className="min-h-0 flex-1">
        <PortfolioGeoSection
          customers={customers}
          filters={filters}
          customerIds={effectiveCustomerIds}
          className="h-full"
        />
      </Tabs.Content>

      <Tabs.Content value="mcp" className="min-h-0 flex-1 overflow-y-auto">
        <PortfolioMcpSection
          customerIds={effectiveCustomerIds}
          excludeCustomerIds={filters.excludeCustomerIds}
          excludeUserIds={filters.excludeUserIds}
        />
      </Tabs.Content>
    </Tabs>
  )
}

export default PortfolioSubTabs
